import clsx from "clsx";
import React, { useRef } from "react";
import {
  Card,
  CardProps,
  Typography,
  Box,
  IconButton,
  Button,
} from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";

import {
  JourneyAnchors,
  LastActiveStatus,
  Units,
  UserRole,
} from "../../constants";
import { colorSystem } from "../../theme";
import {
  formatAgeAndGender,
  formatWeight,
  formatHeight,
} from "../../utils/units";

import { useCurrentUser, UserInfo } from "../../hooks/useCurrentUser";
import { CardAvatar } from "../card/CardAvatar";

import { useNavigate } from "react-router-dom";
import { COACH_CLIENTS_ARCHIVED_ROUTE } from "../../routes/routes";
import { IBriefClientInfo } from "@growth-machine-llc/stridist-api-client";
import { useToastAlert } from "../app/ToastAlert/ToastAlertProvider";
import ClientContextMenu from "./ClientContextMenu";
import useDeleteClientsMutation from "./mutations/useDeleteClientsMutation";
import dayjs from "dayjs";
import { TimeAgo } from "../../routes/utils/TimeAgo";
import useArchiveSingleClientMutation from "./mutations/useArchiveSingleClientMutation";
import { useSampleSetup } from "./hooks/useSampleSetup";
import { Tooltip } from "../info/Tooltip";

import { Sparkles } from "lucide-react";
import { usePopupState } from "material-ui-popup-state/hooks";
import { ConfirmActionDialog } from "../dialog/ConfirmActionDialog";
import { ReactComponent as AgeIcon } from "../../icons/Age.svg";
import { ReactComponent as WeightIcon } from "../../icons/bodyWeight.svg";
import { ReactComponent as HeightIcon } from "../../icons/Height.svg";
import { ProfileIconBlock } from "./ProfileIconBlock";
import { useContainerQuery } from "../../hooks/useContainerQuery";
import { PreviewType, usePreview } from "../../hooks/usePreview";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    display: "block",
    containerType: "inline-size",
    boxSizing: "border-box",
    backgroundColor: "inherit",
  },
  root: {
    position: "relative",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "inherit",
    boxShadow: "none",
    padding: 0,
    borderRadius: 0,
    width: "100%",
    boxSizing: "border-box",
    [theme.containerQueries.down("md")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(2),
    },
  },

  content: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    width: "100%",
    backgroundColor: "inherit",
  },

  avatar: {
    width: 120,
    height: 120,
    margin: 0,
    borderRadius: 4,
    flexShrink: 0,
  },

  info: {
    display: "flex",
    flexDirection: "column",
    margin: theme.spacing(0, 3, 0, 2),
    [theme.containerQueries.down("md")]: {
      margin: theme.spacing(0, 0, 0, 1.5),
    },
  },

  header: {
    fontSize: 24,
    fontWeight: 600,
    lineHeight: "29px",
    paddingBottom: theme.spacing(1),
    color: theme.palette.text.primary,
    [theme.containerQueries.down("md")]: {
      fontSize: 16,
      lineHeight: "20px",
    },
  },

  text: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "17px",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0),
  },

  topMargin: {
    marginTop: theme.spacing(0.5),
  },

  metrics: {
    padding: theme.spacing(2.5, 2, 2),
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.action.hover,
    width: "100%",
    maxWidth: 500,
    boxSizing: "border-box",
    [theme.containerQueries.down("md")]: {
      maxWidth: "100%",
      marginTop: theme.spacing(1),
    },
  },

  metricWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },

  metricValue: {
    fontSize: 18,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },

  more: {
    color: theme.palette.text.secondary,
    position: "absolute",
    top: theme.spacing(0),
    right: theme.spacing(0),
    [theme.containerQueries.down("md")]: {
      top: theme.spacing(-1),
      right: theme.spacing(-1),
    },
  },

  userMenuItem: {
    padding: theme.spacing(0),
    margin: theme.spacing(1, 0),
    cursor: "pointer",
    "&:hover *": {
      color: theme.palette.text.primary,
    },
  },

  userMenuText: {
    "& > span": {
      fontWeight: "bold",
      fontSize: 15,
    },
  },
  circle: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: theme.palette.text.secondary,
  },
  infoUser: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    [theme.containerQueries.down("sm")]: {
      gap: theme.spacing(1),
    },
  },

  active: {
    backgroundColor: colorSystem.green,
  },
  recently: {
    backgroundColor: colorSystem.yellow,
  },
  away: {
    backgroundColor: colorSystem.gray,
  },

  sampleSetupButton: {
    padding: theme.spacing(1.5, 1),
    fontSize: 12,
    height: 20,
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const getLastActiveStatus = (
  lastActiveAt: dayjs.Dayjs | null,
): LastActiveStatus => {
  if (lastActiveAt) {
    const now = dayjs();
    const diffMinutes = now.diff(lastActiveAt, "minute");
    const diffDays = now.diff(lastActiveAt, "day");

    if (diffMinutes < 10) {
      return LastActiveStatus.ACTIVE;
    } else if (diffMinutes < 60 * 2) {
      return LastActiveStatus.RECENTLY;
    } else if (diffDays < 100) {
      return LastActiveStatus.AWAY;
    }
  }
  return LastActiveStatus.NO_DATA;
};

const getLastActiveMessage = (status: LastActiveStatus): string => {
  const lastSeenMessage = "last seen";
  switch (status) {
    case LastActiveStatus.ACTIVE:
      return `${lastSeenMessage} just now`;
    case LastActiveStatus.RECENTLY:
      return `${lastSeenMessage} recently`;
    case LastActiveStatus.AWAY:
      return lastSeenMessage;
    default:
      return `${lastSeenMessage} a long time ago`;
  }
};

export interface ClientSummaryProps extends CardProps {
  client:
    | (Omit<UserInfo, "phoneCode"> & Omit<IBriefClientInfo, "id">)
    | IBriefClientInfo;
}

export function ClientSummary(props: ClientSummaryProps) {
  const { className, client, ...other } = props;
  const s = useStyles();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const {
    id: clientId,
    displayName,
    photoUrl: photoURL,
    email,
    age,
    gender,
    weight,
    height,
    units,
    location,
    status,
    lastActiveAt,
    username,
  } = client;

  const { mutate: setupSample, isPending: isPendingSetupSample } =
    useSampleSetup({ clientId, username });

  const containerRef = useRef<HTMLDivElement>(null);

  const isMobile = useContainerQuery(containerRef, (theme) =>
    theme.containerQueries.down("md"),
  );
  const [preview] = usePreview();
  const isMobilePreview = preview === PreviewType.MOBILE;

  const confirmSetupSampleClientDialogState = usePopupState({
    variant: "popover",
    popupId: "sample-setup-client",
  });

  const initials = displayName.substring(0, 1);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { showToastAlert } = useToastAlert();

  const isSample = user.accounts.some(
    (account) => client.id === account.id && account.isSample,
  );

  const { mutate: toggleArchiveClient, isPending: archivingClient } =
    useArchiveSingleClientMutation(username);

  const { mutate: deleteArchivedClient, isPending: deletingClient } =
    useDeleteClientsMutation(status);

  const handleDelete = React.useCallback(() => {
    deleteArchivedClient(
      { clientIds: [clientId] },
      {
        onSuccess() {
          navigate(COACH_CLIENTS_ARCHIVED_ROUTE);
        },
      },
    );
  }, [deleteArchivedClient, clientId]);

  const details = React.useMemo(
    () => [location || "No location to show", email],
    [location, email],
  );

  const metrics = React.useMemo(
    () => [
      {
        name: "Age",
        value: formatAgeAndGender(age ?? "-", gender),
        icon: <AgeIcon />,
      },
      {
        name: "Weight",
        value: formatWeight(weight, units as Units),
        icon: <WeightIcon />,
      },
      {
        name: "Height",
        value: formatHeight(height, units as Units),
        icon: <HeightIcon />,
      },
    ],
    [age, gender, weight, height, units],
  );

  const handleMoreClick = React.useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      setAnchorEl(anchorEl ? null : event.currentTarget);
    },
    [anchorEl],
  );

  const handleMoreClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  const onArchivedChange = React.useCallback(
    (archived: boolean) => {
      toggleArchiveClient(
        {
          clientId: +clientId,
          archived,
        },
        {
          onSuccess: () => {
            const message = archived ? "Client archived" : "Client restored";
            showToastAlert("success", { message });
          },
        },
      );
    },
    [clientId, showToastAlert, toggleArchiveClient],
  );

  const lastActiveToDeterminateStatus = lastActiveAt
    ? dayjs(lastActiveAt.toString())
    : null;

  const lastActiveStatus = getLastActiveStatus(lastActiveToDeterminateStatus);
  const lastActiveMessage = getLastActiveMessage(lastActiveStatus);

  const handleSetupSampleClient = React.useCallback(() => {
    setupSample({});
    confirmSetupSampleClientDialogState.close();
  }, [setupSample, confirmSetupSampleClientDialogState]);

  return (
    <Box ref={containerRef} className={s.container}>
      <Box className={clsx(s.root, className)} {...other}>
        <Box className={clsx(s.content, JourneyAnchors.CLIENT_INFO)}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <CardAvatar className={s.avatar} src={photoURL} variant="rounded">
              {initials}
            </CardAvatar>
            <Box className={s.info}>
              <Box display={"flex"} flexDirection="column" gap={1.5}>
                <Box className={s.infoUser}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      className={clsx(s.circle, {
                        [s.active]:
                          lastActiveStatus === LastActiveStatus.ACTIVE,
                        [s.recently]:
                          lastActiveStatus === LastActiveStatus.RECENTLY,
                        [s.away]:
                          lastActiveStatus === LastActiveStatus.AWAY ||
                          lastActiveStatus === LastActiveStatus.NO_DATA,
                      })}
                    />
                    <Typography
                      variant="body1"
                      className={clsx(s.text)}
                      children={
                        LastActiveStatus.AWAY === lastActiveStatus ? (
                          <>
                            last seen <TimeAgo date={lastActiveAt} />
                          </>
                        ) : (
                          lastActiveMessage
                        )
                      }
                    />
                  </Box>

                  {isSample && user.role === UserRole.COACH && (
                    <Tooltip title="Click to activate the sample" arrow>
                      <Button
                        className={s.sampleSetupButton}
                        startIcon={<Sparkles height={16} width={16} />}
                        variant="contained"
                        color="inherit"
                        disabled={isPendingSetupSample}
                        onClick={() =>
                          confirmSetupSampleClientDialogState.open()
                        }
                      >
                        Activate
                      </Button>
                    </Tooltip>
                  )}
                </Box>
                <Box>
                  <Typography variant="h6" className={s.header}>
                    {displayName}
                  </Typography>
                  {details.map((detail, index) => (
                    <Typography
                      key={index}
                      variant="body1"
                      className={clsx(s.text, s.topMargin)}
                      children={detail}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box className={clsx(s.metrics, JourneyAnchors.CLIENT_METRICS)}>
          {metrics.map((metric, index) => (
            <Box key={index} className={s.metricWrapper}>
              {!isMobile && <ProfileIconBlock icon={metric.icon} />}
              <Box display="flex" flexDirection="column" alignItems="center">
                <Typography
                  variant="body1"
                  className={s.text}
                  children={metric.name}
                />
                <Typography
                  variant="body1"
                  className={s.metricValue}
                  children={metric.value}
                />
              </Box>
            </Box>
          ))}
        </Box>

        {user.role === UserRole.COACH && !isMobilePreview && (
          <>
            <IconButton
              aria-label="settings"
              className={s.more}
              onClick={handleMoreClick}
              children={<MoreVert />}
              size="large"
            />

            <ClientContextMenu
              anchorEl={anchorEl}
              handleClose={handleMoreClose}
              archivingClient={archivingClient}
              onArchivedChange={onArchivedChange}
              deletingClient={deletingClient}
              onDelete={handleDelete}
              user={client}
              isSample={isSample}
            />
          </>
        )}

        {confirmSetupSampleClientDialogState.isOpen && (
          <ConfirmActionDialog
            title={`See Stridist in action`}
            subtitle={
              <Box sx={{ fontSize: 16 }}>
                Wonder how your client dashboard will look?
              </Box>
            }
            description={
              <>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Box>
                    Activate your sample client to explore how Stridist tracks
                    progress, workouts, and insights.
                  </Box>

                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    If Vanessa has already completed tasks for today and recent
                    days, this action might have no effect.
                  </Typography>
                </Box>
              </>
            }
            open
            confirmButtonText="Activate"
            onClose={confirmSetupSampleClientDialogState.close}
            onCancel={confirmSetupSampleClientDialogState.close}
            onConfirm={handleSetupSampleClient}
          />
        )}
      </Box>
    </Box>
  );
}
